import { useState, type ReactNode } from "react";
import { parseBlockContent } from "../../../lib/blockContentSchemas";
import type { BlockEditPatch, EditorBlock } from "../editor.types";
import { RichText } from "../document/RichText";
import { EditActions } from "./BlockEditControls";
import { editInputClass, onEscapeCancel } from "../editUtils";

type TextBlockProps = {
  block: EditorBlock;
  editing?: boolean;
  onSave?: (patch: BlockEditPatch) => Promise<string | null>;
  onCancelEdit?: () => void;
};

export const TextBlock = ({
  block,
  editing,
  onSave,
  onCancelEdit,
}: TextBlockProps): ReactNode => {
  const content = parseBlockContent("text", block.content);
  const text =
    typeof content.richText === "string"
      ? content.richText
      : JSON.stringify(content.richText);

  if (editing) {
    return (
      <TextBlockEditor onCancel={onCancelEdit} onSave={onSave} text={text} />
    );
  }

  return <RichText value={content.richText} />;
};

type TextBlockEditorProps = {
  text: string;
  onSave?: (patch: BlockEditPatch) => Promise<string | null>;
  onCancel?: () => void;
};

const TextBlockEditor = ({ text, onSave, onCancel }: TextBlockEditorProps) => {
  const [draft, setDraft] = useState(text);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    const result = await onSave?.({ content: { richText: draft } });
    setSaving(false);
    if (result) setError(result);
  };

  return (
    <div className="space-y-3" onKeyDown={onEscapeCancel(onCancel)}>
      <textarea
        autoFocus
        className={`${editInputClass} resize-y text-lg leading-8`}
        onChange={(event) => setDraft(event.target.value)}
        rows={Math.min(Math.max(draft.split("\n").length, 3), 12)}
        value={draft}
      />
      <EditActions
        error={error}
        onCancel={onCancel}
        onSave={save}
        saving={saving}
      />
    </div>
  );
};
